/**
 * React Query key factory — GED « entité » (INSTR-009).
 *
 * Généralise les anciennes chaînes brutes `['client-documents', id]` du hook
 * CLI-017 en une factory paramétrée par le `kind` de l'entité porteuse. Le `kind`
 * est DANS la clé : une invalidation racine 'client' ne doit JAMAIS toucher un
 * cache 'instructor' (et réciproquement).
 *
 * `scope(kind, id)` est le préfixe commun → invalider `scope(...)` invalide d'un
 * coup la liste (`docs`), l'arbre généré (`tree`) et les dossiers (`folders`).
 */

import type { EntityDocumentFilters } from '@/types/documents/entity-documents'

// ⚠️ **Ce type DOIT rester égal à `EntityKind` de `useEntityDocuments.ts`.**
// Ce sont deux déclarations de la même liste : le hook borne les URL, celle-ci
// borne les clés de cache. En désaccord, `tsc` le dit — c'est d'ailleurs lui
// qui a trouvé cette seconde source de vérité quand `resource` est arrivé.
export type EntityDocKind = 'client' | 'instructor' | 'nonconformity' | 'resource'

const entityDocsAll = ['entity-documents'] as const

export const entityDocumentsKeys = {
    all: entityDocsAll,
    scope: (kind: EntityDocKind, id: string) => [...entityDocsAll, kind, id] as const,
    docs: (kind: EntityDocKind, id: string, filters?: EntityDocumentFilters) =>
        [...entityDocsAll, kind, id, 'list', filters ?? {}] as const,
    tree: (kind: EntityDocKind, id: string) => [...entityDocsAll, kind, id, 'tree'] as const,
    folders: (kind: EntityDocKind, id: string) => [...entityDocsAll, kind, id, 'folders'] as const,
}

/** Namespace exposé dans le barrel `@/lib/queryKeys`. */
export const documentsKeys = {
    entityDocuments: entityDocumentsKeys,
} as const
